import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { toast } from 'react-hot-toast';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

interface ConnectAccountProps {}

const ConnectAccount: React.FC<ConnectAccountProps> = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [accountStatus, setAccountStatus] = useState<string>('');
  const [balance, setBalance] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchAccountStatus = useCallback(async () => {
    if (!user?.uid) return;
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setAccountStatus(userData.stripeAccountStatus || 'not_created');
      }
    } catch (error) {
      console.error('Error fetching account status:', error);
      toast.error('Failed to fetch account status');
    }
  }, [user?.uid]);

  const fetchUserBalance = useCallback(async () => {
    if (!user?.uid) return;
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setBalance(userData.accountBalance || 0);
      }
    } catch (error) {
      console.error('Error fetching user balance:', error);
      toast.error('Failed to fetch balance');
    }
  }, [user?.uid]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchAccountStatus(), fetchUserBalance()]);
      setLoading(false);
    };
    
    if (user?.uid) {
      loadData();
    }
  }, [user?.uid, fetchAccountStatus, fetchUserBalance]);

  const handleCreateAccount = async () => {
    if (!user?.uid) {
      toast.error('Please log in to create a Stripe account');
      return;
    }

    try {
      const response = await fetch('/api/connect/create-account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId: user.uid }),
      });

      if (!response.ok) {
        throw new Error('Failed to create Stripe account');
      }

      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Error creating Stripe account:', error);
      toast.error('Failed to create Stripe account');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium mb-4">Stripe Connect Account</h3>
        {accountStatus === 'not_created' ? (
          <div>
            <p className="text-gray-600 mb-4">
              To withdraw funds, you need to create a Stripe Connect account.
            </p>
            <button
              onClick={handleCreateAccount}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Create Stripe Account
            </button>
          </div>
        ) : (
          <div>
            <p className="text-gray-600">
              Account Status: <span className="font-medium">{accountStatus}</span>
            </p>
            <p className="text-gray-600 mt-2">
              Available Balance: <span className="font-medium">${balance.toFixed(2)}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConnectAccount; 